import { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Flex, Text, Button, Stack, HStack, Badge } from "@chakra-ui/react";
import { FileSignature } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { apiPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeAll } from "@/lib/offline";
import { Modal } from "@/components/Modal";
import { showToast } from "@/components/AppToaster";
import { colors } from "@/theme/colors";

const Sel = ({ value, onChange, children, testId }) => (
  <Box as="select" value={value} onChange={onChange} data-testid={testId} w="full" px="3" py="2.5"
    borderRadius="8px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm">{children}</Box>
);
const Lbl = ({ children }) => <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>{children}</Text>;
const inp = { width: "100%", padding: "10px 12px", borderRadius: "8px", border: `1px solid ${colors.border}`, fontSize: "14px", background: "#fff" };

// Read-and-sign requests live in the Resources tab. `resource` pre-locks the
// dialog to that document; without it the picker offers company + Hakina docs.
export function RequestAckButton({ resource = null, size = "sm", variant = "solid" }) {
  const { role } = useAuth();
  const [open, setOpen] = useState(false);
  if (!["org_admin", "supervisor"].includes(role)) return null;
  return (
    <>
      <Button size={size} variant={variant} bg={variant === "solid" ? colors.teal : undefined}
        color={variant === "solid" ? "white" : colors.textPrimary}
        _hover={{ bg: variant === "solid" ? colors.tealHover : colors.cream }}
        onClick={() => setOpen(true)}
        data-testid={resource ? `request-ack-${resource.id}` : "request-ack-btn"}>
        <FileSignature size={15} /> {resource ? "Request" : "+ Request Acknowledgement"}
      </Button>
      {open && <RequestAckModal resource={resource} onClose={() => setOpen(false)} />}
    </>
  );
}

export function RequestAckModal({ resource, onClose }) {
  const { user, activeCompanyId } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [resources, setResources] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [f, setF] = useState({
    resource_id: resource?.id || "", worker_ids: [], signer_ids: [],
    instructions: "", due_date: today,
    recurrence: "once", recurrence_end: "", occurrences: "",
  });

  const load = useCallback(async () => {
    // Tenant isolation (Sweep A4): pickers pinned to the ACTING company —
    // workers of this company only; documents = own company + Hakina global.
    const [rRes, wRes] = await safeAll([
      resource ? Promise.resolve({ data: [] })
        : supabase.from("resources").select("id,title,version,company_id")
            .or(`company_id.is.null,company_id.eq.${activeCompanyId}`).order("title"),
      supabase.from("workers").select("id,full_name,job_role").eq("status", "active")
        .eq("company_id", activeCompanyId).not("profile_id", "is", null).order("full_name"),
    ]);
    if (wRes.error || (!resource && rRes.error)) {
      // Offline: pickers fall back to the same cached options as the screens.
      const snap = cacheGet(user.id, "pickers") || {};
      setResources(resource ? [] : (snap.ackResources || []));
      setWorkers(snap.workers || []);
    } else {
      setResources(rRes.data || []);
      setWorkers(wRes.data || []);
      cachePut(user.id, "pickers", {
        ...(cacheGet(user.id, "pickers") || {}),
        workers: wRes.data || [],
        ...(resource ? {} : { ackResources: rRes.data || [] }),
      });
    }
    setReady(true);
  }, [resource, user.id, activeCompanyId]);
  useEffect(() => { load(); }, [load]);

  const toggle = (key, id) => setF((s) => ({ ...s, [key]: s[key].includes(id) ? s[key].filter((x) => x !== id) : [...s[key], id] }));
  const availableSigners = useMemo(() => workers.filter((w) => !f.signer_ids.includes(w.id)), [workers, f.signer_ids]);
  const pickedSigners = useMemo(() => workers.filter((w) => f.signer_ids.includes(w.id)), [workers, f.signer_ids]);

  const submit = async () => {
    setErr(null);
    if (!f.resource_id) { setErr("Choose a document."); return; }
    if (f.worker_ids.length === 0) { setErr("Assign at least one worker."); return; }
    setBusy(true);
    try {
      const signers = workers.filter((w) => f.signer_ids.includes(w.id)).map((w) => ({ worker_id: w.id, name: w.full_name }));
      const res = await apiPost("/assignments/create", {
        kind: "resource", resource_id: f.resource_id, template_id: null, location_id: null,
        worker_ids: f.worker_ids, instructions: f.instructions.trim() || null,
        additional_signers: signers, recurrence: f.recurrence, due_date: f.due_date,
        recurrence_end: f.recurrence === "once" ? null : (f.recurrence_end || null),
        occurrences: f.recurrence === "once" ? null : (f.occurrences ? Number(f.occurrences) : null),
        redirect_to: window.location.origin,
      });
      showToast(`Acknowledgement requested — ${res.created} task${res.created === 1 ? "" : "s"} assigned${res.emailed ? `, ${res.emailed} notified` : ""}`);
      onClose();
    } catch (e) {
      setErr(navigator.onLine ? e.message : "You're offline — this action needs a connection. Your selections stay on screen; try again once you reconnect.");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Request acknowledgement" testId="request-ack-modal">
      <Stack gap="3.5">
        {err && <Box px="3" py="2" borderRadius="8px" bg={colors.criticalBg} color={colors.criticalText} fontSize="sm" data-testid="request-ack-error">{err}</Box>}

        <Box>
          <Lbl>Document</Lbl>
          {resource ? (
            <Flex px="3" py="2.5" borderRadius="8px" bg={colors.cream} align="center" gap="2" data-testid="request-ack-locked-resource">
              <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{resource.title}</Text>
              <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" fontSize="10px">v{resource.version || 1}</Badge>
            </Flex>
          ) : (
            <Sel value={f.resource_id} onChange={(e) => setF({ ...f, resource_id: e.target.value })} testId="request-ack-resource-select">
              <option value="">{ready ? "Choose a document…" : "Loading…"}</option>
              {resources.map((r) => (
                <option key={r.id} value={r.id}>{`${r.title} (v${r.version || 1})${r.company_id === null ? " — Hakina" : ""}`}</option>
              ))}
            </Sel>
          )}
          <Text fontSize="xs" color={colors.textSecondary} mt="1">The request locks to the document's current version at send time.</Text>
        </Box>

        <Box>
          <Lbl>Assign Worker(s)</Lbl>
          <Stack gap="1" maxH="150px" overflowY="auto" border="1px solid" borderColor={colors.border} borderRadius="8px" p="2" data-testid="request-ack-workers">
            {workers.map((w) => (
              <HStack key={w.id} as="button" type="button" onClick={() => toggle("worker_ids", w.id)} gap="2" px="2" py="1.5" borderRadius="6px"
                bg={f.worker_ids.includes(w.id) ? colors.tealTint : "transparent"} _hover={{ bg: colors.cream }} data-testid={`request-ack-worker-${w.id}`}>
                <Box w="16px" h="16px" borderRadius="4px" border="1.5px solid" borderColor={f.worker_ids.includes(w.id) ? colors.teal : colors.border} bg={f.worker_ids.includes(w.id) ? colors.teal : "white"} />
                <Text fontSize="sm" color={colors.textPrimary}>{w.full_name}{w.job_role ? ` · ${w.job_role}` : ""}</Text>
              </HStack>
            ))}
          </Stack>
          {f.worker_ids.length > 0 && <Text fontSize="xs" color={colors.textSecondary} mt="1">{f.worker_ids.length} selected</Text>}
        </Box>

        <Box>
          <Lbl>Due date (optional deadline)</Lbl>
          <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} style={inp} data-testid="request-ack-due-date" />
        </Box>

        <Box>
          <Lbl>Instructions (optional)</Lbl>
          <textarea value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} rows={2} style={{ ...inp, resize: "vertical" }} placeholder="Note to the assignees…" data-testid="request-ack-instructions" />
        </Box>

        <Box>
          <Lbl>Additional required signers (optional)</Lbl>
          <Sel value="" onChange={(e) => { if (e.target.value) toggle("signer_ids", e.target.value); }} testId="request-ack-signers">
            <option value="">Add a required signer…</option>
            {availableSigners.map((w) => <option key={w.id} value={w.id}>{w.full_name}</option>)}
          </Sel>
          {pickedSigners.length > 0 && (
            <Flex gap="1.5" flexWrap="wrap" mt="2">
              {pickedSigners.map((w) => (
                <Badge key={w.id} bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5" cursor="pointer" onClick={() => toggle("signer_ids", w.id)}>{w.full_name} ✕</Badge>
              ))}
            </Flex>
          )}
        </Box>

        <Box>
          <Lbl>Recurrence (periodic re-attestation)</Lbl>
          <Sel value={f.recurrence} onChange={(e) => setF({ ...f, recurrence: e.target.value })} testId="request-ack-recurrence">
            <option value="once">One-off (by due date)</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </Sel>
          {f.recurrence !== "once" && (
            <HStack gap="2" mt="2">
              <Box flex="1">
                <Text fontSize="xs" color={colors.textSecondary} mb="1">End date</Text>
                <input type="date" value={f.recurrence_end} onChange={(e) => setF({ ...f, recurrence_end: e.target.value, occurrences: "" })} style={inp} data-testid="request-ack-end-date" />
              </Box>
              <Text fontSize="xs" color={colors.textSecondary} pt="4">or</Text>
              <Box flex="1">
                <Text fontSize="xs" color={colors.textSecondary} mb="1"># occurrences</Text>
                <input type="number" min="1" max="52" value={f.occurrences} onChange={(e) => setF({ ...f, occurrences: e.target.value, recurrence_end: "" })} style={inp} placeholder="e.g. 4" data-testid="request-ack-occurrences" />
              </Box>
            </HStack>
          )}
        </Box>

        <Button onClick={submit} loading={busy} data-testid="request-ack-submit">Send request</Button>
      </Stack>
    </Modal>
  );
}
